import {createFeatureSelector, createSelector, MemoizedSelector} from '@ngrx/store';
import {SeFePaginationOptions} from 'se-fe-pagination';
import {ProductManagementErrorInterface} from '../../../error/error-handling/product-management-error';
import {normalizeProductQueryParams, ProductQueryParams, ProductQueryParamsInput} from '../../../models';
import {CollectionMetadata, Product} from '../../../models/clare';
import {selectQueryParams} from '../../../state/router/router.selectors';
import {buildPaginationOptions} from '../../../utils/pagination-utils';
import * as fromProduct from './product-list.reducer';

export const selectProductState = createFeatureSelector<fromProduct.ProductState>(fromProduct.productFeatureKey);

export const selectProductListLoading = createSelector(selectProductState, fromProduct.getProductLoadingState);

export const selectProducts: MemoizedSelector<object, Product[]> = createSelector(
  selectProductState,
  fromProduct.getProducts
);

export const selectClareAccountStatus = createSelector(selectProductState, fromProduct.getClareAccountStatus);

export const selectPaginationMeta: MemoizedSelector<object, CollectionMetadata> = createSelector(
  selectProductState,
  fromProduct.getPaginationMeta
);

export const selectProductPaginationOptions: MemoizedSelector<object, SeFePaginationOptions> = createSelector(
  selectPaginationMeta,
  paginationMeta => buildPaginationOptions(paginationMeta, {label: 'Products'})
);

export const selectError: MemoizedSelector<object, ProductManagementErrorInterface | undefined> = createSelector(
  selectProductState,
  fromProduct.getError
);

export const selectProductQuery: MemoizedSelector<object, string> = createSelector(
  selectQueryParams,
  (params): string => params?.['query'] ?? ''
);

export const selectAllQueryParamsFromUrl = createSelector(selectQueryParams, (params): ProductQueryParams =>
  normalizeProductQueryParams({
    page: params?.['page'],
    take: params?.['take'],
    query: params?.['query'] ?? '',
    status: params?.['status'],
    catalogId: params?.['catalogId'] ?? params?.['catalog_id'],
    sortBy: params?.['sortBy'],
    direction: params?.['direction'],
  } as ProductQueryParamsInput)
);

export const selectProductSortBy = createSelector(selectAllQueryParamsFromUrl, params => params.sortBy);

export const selectProductSortDirection = createSelector(selectAllQueryParamsFromUrl, params => params.direction);

export const selectProductActive = createSelector(selectAllQueryParamsFromUrl, params => params.active);

export const selectProductCatalogId = createSelector(selectAllQueryParamsFromUrl, params => params.catalogId);

/** Products only (no bundles), for the choose-bundle-products modal. */
export const selectProductsForSelection: MemoizedSelector<object, Product[]> = createSelector(
  selectProductState,
  fromProduct.getSelectionProducts
);

export const selectPaginationMetaForSelection: MemoizedSelector<object, CollectionMetadata> = createSelector(
  selectProductState,
  fromProduct.getSelectionPaginationMeta
);

export const selectProductListLoadingForSelection = createSelector(selectProductState, fromProduct.getSelectionLoading);
